import { reactive } from 'vue'

// All prices in the app's data (packages, hotels) are stored in PKR.
// Rates below are approximate PKR-per-1-unit, used only to display an
// equivalent price in the visitor's preferred currency -- not live rates.
export const CURRENCIES = [
  { code: 'PKR', symbol: '₨', label: 'Pakistani Rupee', rate: 1 },
  { code: 'SAR', symbol: '﷼', label: 'Saudi Riyal', rate: 74 },
  { code: 'USD', symbol: '$', label: 'US Dollar', rate: 278 },
  { code: 'GBP', symbol: '£', label: 'British Pound', rate: 353 }
]

const LS_KEY = 'etimad_currency'

function readStoredCode() {
  try {
    const stored = localStorage.getItem(LS_KEY)
    if (stored && CURRENCIES.some(c => c.code === stored)) return stored
  } catch (e) {
    // localStorage unavailable -- fall back to default below
  }
  return 'PKR'
}

export const currencyState = reactive({
  code: readStoredCode()
})

export function setCurrency(code) {
  if (!CURRENCIES.some(c => c.code === code)) return
  currencyState.code = code
  try {
    localStorage.setItem(LS_KEY, code)
  } catch (e) {
    // localStorage unavailable -- selection just won't persist
  }
}

export function getCurrentCurrency() {
  return CURRENCIES.find(c => c.code === currencyState.code) || CURRENCIES[0]
}

// Formats amountInPkr (a number already stored in PKR) using a specific
// currency code, regardless of the site-wide visitor currency switcher --
// used e.g. for invoices, which are issued in a currency the admin picks
// per document rather than whatever a site visitor currently has selected.
export function formatPriceIn(amountInPkr, code) {
  const currency = CURRENCIES.find(c => c.code === code) || CURRENCIES[0]
  const converted = (Number(amountInPkr) || 0) / currency.rate
  const decimals = currency.code === 'PKR' ? 0 : 2
  const formatted = converted.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })
  return `${currency.symbol} ${formatted}`
}

// amountInPkr: a number already stored in PKR (the site's base currency)
export function formatPrice(amountInPkr) {
  return formatPriceIn(amountInPkr, currencyState.code)
}

// Formats `amount` as already being IN the given currency -- no conversion.
// Used for documents like invoices, where the admin types figures directly
// in whichever currency they're billing (e.g. entering SAR amounts once the
// invoice's currency is set to SAR), rather than always typing PKR and
// having it converted for display.
export function formatAmount(amount, code) {
  const currency = CURRENCIES.find(c => c.code === code) || CURRENCIES[0]
  const decimals = currency.code === 'PKR' ? 0 : 2
  const formatted = (Number(amount) || 0).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })
  return `${currency.symbol} ${formatted}`
}
